import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Zap } from "lucide-react";

const ELECTIONS = [
  {
    key: "sa2026",
    label: "SA 2026",
    date: new Date("2026-03-21T08:00:00+09:30"),
    enrolClose: new Date("2026-03-03T23:59:59+09:30"),
    enrolText: "Enrol by Mar 3",
    detail: "March 21 · Malinauskas vs Speirs",
    path: "/sa-2026",
    accentColor: "#F43F5E",
    accentBg: "rgba(244, 63, 94, 0.10)",
  },
  {
    key: "vic2026",
    label: "VIC 2026",
    date: new Date("2026-11-28T08:00:00+11:00"),
    enrolClose: new Date("2026-11-13T23:59:59+11:00"),
    enrolText: "Enrol by Nov 13",
    detail: "November 28 · Allan vs Pesutto",
    path: "/vic-2026",
    accentColor: "#FACC15",
    accentBg: "rgba(250, 204, 21, 0.10)",
  },
];

export function ElectionBanner() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("election_banner_v2_dismissed") === "true"; }
    catch { return false; }
  });

  if (dismissed) return null;

  const now = Date.now();

  const active = ELECTIONS.find((e) => {
    const msTo = e.date - now;
    return msTo > 0 && msTo < 60 * 86400000;
  });

  if (!active) return null;

  const daysTo = Math.floor((active.date - now) / 86400000);
  const daysToEnrol = Math.floor((active.enrolClose - now) / 86400000);
  const enrolClosed = daysToEnrol < 0;
  const enrolUrgent = !enrolClosed && daysToEnrol <= 5;
  const imminent = daysTo <= 7;

  return (
    <div
      data-testid="election-banner"
      style={{
        background: imminent
          ? "linear-gradient(90deg, rgba(244,63,94,0.18) 0%, rgba(8,14,26,0.95) 100%)"
          : "linear-gradient(90deg, rgba(250,204,21,0.10) 0%, rgba(8,14,26,0.95) 100%)",
        borderBottom: "1px solid rgba(51,65,85,0.45)",
        padding: "8px 16px",
        position: "sticky",
        top: 61,
        zIndex: 45,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <div style={{
        maxWidth: 1280,
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        minWidth: 0,
      }}>
        {/* Left — core info always visible */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "2px 10px",
            borderRadius: 99,
            background: active.accentColor,
            flexShrink: 0,
          }}>
            <Zap style={{ width: 11, height: 11, color: "#000", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#000", fontFamily: "Outfit, sans-serif", whiteSpace: "nowrap" }}>
              {active.label}
            </span>
          </div>

          <span style={{ fontSize: 12, fontWeight: 500, color: "#94A3B8", fontFamily: "Public Sans, sans-serif", whiteSpace: "nowrap" }}>
            {active.detail} ·{" "}
            <span style={{ fontWeight: 700, color: imminent ? active.accentColor : "#FACC15" }}>
              {daysTo === 0 ? "TODAY" : `${daysTo}d`}
            </span>
          </span>

          {!enrolClosed && (
            <span
              className="hidden sm:inline-flex"
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 99,
                background: enrolUrgent ? "rgba(244,63,94,0.12)" : "rgba(51,65,85,0.35)",
                border: `1px solid ${enrolUrgent ? "rgba(244,63,94,0.25)" : "rgba(51,65,85,0.45)"}`,
                color: enrolUrgent ? "#F43F5E" : "#94A3B8",
                whiteSpace: "nowrap",
              }}
            >
              {daysToEnrol === 0 ? "Enrol TODAY" : `${active.enrolText} (${daysToEnrol}d)`}
            </span>
          )}

          <a
            href="https://www.aec.gov.au/enrol/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex"
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 99,
              background: "rgba(226,232,240,0.08)",
              border: "1px solid rgba(226,232,240,0.15)",
              color: "#CBD5E1",
              textDecoration: "none",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            Check Enrolment →
          </a>
        </div>

        {/* Right */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          <button
            data-testid="banner-guide-btn"
            onClick={() => { navigate(active.path); window.scrollTo(0, 0); }}
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 12px",
              borderRadius: 99,
              background: active.accentColor,
              border: "none",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.15s",
              fontFamily: "Outfit, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            <span className="hidden sm:inline">{active.label} Full Guide →</span>
            <span className="sm:hidden">Guide →</span>
          </button>
          <button
            aria-label="Dismiss banner"
            onClick={() => {
              setDismissed(true);
              try { localStorage.setItem("election_banner_v2_dismissed", "true"); } catch {}
            }}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: "#64748B",
              lineHeight: 1,
              borderRadius: 6,
              transition: "color 0.15s",
            }}
          >
            <X style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>
    </div>
  );
}
